<template>
  <Container :title="$gt('Allgemeine Angaben')" :backgroundColor="isDetailview ? 'white' : undefined">
    <v-row>
      <v-col cols="12" sm="6">
        <TextField v-model="formModel.name" :label="$gt('Unternehmensbezeichnung')" :placeholder="$gt('z.B. Mustermann GmbH')" required />
      </v-col>
      <v-col cols="12" sm="6">
        <Select v-model="formModel.legalForm" :label="$gt('Rechtsform')" type="legalForm" required />
      </v-col>
    </v-row>
    <!-- Kundenfeedback 16.10.2026 (Süß): Die Karte „Firmendaten auf einen Blick"
         zeigt diese Adresse als zentrale E-Mail. Sie wurde bisher nur bei der
         Registrierung gesetzt und war danach nirgends änderbar. -->
    <v-row>
      <v-col cols="12" sm="6">
        <TextField
          v-model="formModel.email"
          :label="$gt('Zentrale E-Mail-Adresse')"
          :tooltip="$gt('Erscheint unter Einstellungen › Firmendaten als zentrale E-Mail des Unternehmens. Über diese Adresse erkennt KARS Ihr Unternehmen auch, wenn andere Organisationen es an einem Fall beteiligen.')"
          type="email"
        />
        <MailboxSettingsHint />
      </v-col>
      <!-- Kundenfeedback 16.10.2026 (Antworten auf 14.10., I): Webseite je
           Unternehmen statt nur einer Homepage für die ganze Organisation. -->
      <v-col cols="12" sm="6">
        <TextField
          v-model="formModel.website"
          :label="$gt('Webseite des Unternehmens')"
          :placeholder="$gt('z.B. www.mustermann-kanzlei.de')"
          :tooltip="$gt('Erscheint unter Einstellungen › Firmendaten als Webseite dieses Unternehmens und in E-Mail-Signaturen (Platzhalter Webseite). Leer lassen, wenn das Unternehmen keine eigene Webseite hat.')"
          data-testid="company-website"
        />
      </v-col>
    </v-row>
  </Container>

  <Container :title="$gt('Sitz & Anschrift')" :backgroundColor="isDetailview ? 'white' : undefined">
    <v-alert
      v-if="mode === 'edit' && locationItems.length === 0"
      type="info"
      variant="tonal"
      density="compact"
      class="mb-4"
    >
      {{ $gt('Diesem Unternehmen ist noch') }} <strong>{{ $gt('kein Standort') }}</strong> {{ $gt('zugeordnet. Legen Sie zuerst im Organigramm') }}
      {{ $gt('einen Standort an, der diesem Unternehmen gehört. Anschließend können Sie hier den Hauptsitz auswählen – Anschrift und Kontaktdaten werden dann automatisch übernommen.') }}
    </v-alert>
    <v-alert
      v-else-if="mode === 'edit'"
      type="info"
      variant="tonal"
      density="compact"
      class="mb-4"
    >
      {{ $gt('Die Anschrift eines Unternehmens wird automatisch vom') }} <strong>{{ $gt('Hauptsitz') }}</strong> {{ $gt('übernommen. Wählen Sie unten den Standort aus, der als Hauptsitz dieses Unternehmens gelten soll.') }}
    </v-alert>
    <v-alert
      v-else
      type="info"
      variant="tonal"
      density="compact"
      class="mb-4"
    >
      {{ $gt('Bitte legen Sie zuerst das Unternehmen an. Den Hauptsitz und die Anschrift können Sie danach beim Bearbeiten festlegen, sobald dem Unternehmen ein Standort zugeordnet ist.') }}
    </v-alert>

    <v-row>
      <v-col cols="12" sm="6">
        <Select
          v-model="formModel.idMainLocation"
          @update:modelValue="updateAddress"
          :label="$gt('Hauptsitz')"
          :tooltip="$gt('Der Hauptsitz bestimmt, welche Anschrift und Kontaktdaten das Unternehmen verwendet. Es können nur Standorte ausgewählt werden, die diesem Unternehmen zugeordnet sind.')"
          :items="locationItems"
          item-value="id"
          item-title="name"
          :readonly="mode !== 'edit'"
          :disabled="locationItems.length === 0"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <TextArea
          v-model="formModel.address"
          :label="$gt('Anschrift (vom Hauptsitz)')"
          :tooltip="$gt('Diese Anschrift wird automatisch vom oben ausgewählten Hauptsitz übernommen. Um sie zu ändern, bearbeiten Sie den entsprechenden Standort.')"
          readonly
        />
      </v-col>
    </v-row>
  </Container>

  <Container :title="$gt('Unternehmenslogo')" :backgroundColor="isDetailview ? 'white' : undefined">
    <p class="form-companies__logo-hint">
      {{ $gt('Optional – ohne eigenes Logo wird automatisch das Logo Ihrer Organisation verwendet. Ein hier hochgeladenes Logo ersetzt es für dieses Unternehmen.') }}
    </p>
    <ImagePicker v-model="formModel.logo" :max-file-size-m-b="5" />
  </Container>
</template>

<script>
import Container from "@/components/forms/Container.vue";
import MailboxSettingsHint from "@/pages/pageOrganization/components/MailboxSettingsHint.vue";
import ImagePicker from "@/components/inputs/ImagePicker.vue";
import TextField from "@/components/inputs/TextField.vue";
import Select from "@/components/inputs/select/Select.vue";
import TextArea from "@/components/inputs/TextArea.vue";

import { isEqual, cloneDeep } from "lodash";

export default {
  name: "FormCompanies",

  components: {
    MailboxSettingsHint,
    Container,
    ImagePicker,
    TextField,
    Select,
    TextArea,
  },

  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    locationItems: {
      type: Array,
      required: true,
    },
    isDetailview: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "edit",
    },
  },

  emits: ["update:modelValue"],

  data() {
    return {
      formModel: cloneDeep(this.modelValue),
    };
  },

  watch: {
    modelValue: {
      deep: true,
      handler(newVal) {
        if (!isEqual(newVal, this.formModel)) {
          this.formModel = { ...newVal };
        }
      },
    },
    formModel: {
      deep: true,
      handler(newVal) {
        if (!isEqual(newVal, this.modelValue)) {
          this.$emit("update:modelValue", { ...newVal });
        }
      },
    },
  },

  computed: {},

  methods: {
    updateAddress(newId) {
      const location = this.locationItems.find((l) => l.id === newId);

      if (location) {
        this.formModel.address = location.address;
      } else {
        this.formModel.address = "";
      }
    },
  },
};
</script>

<style scoped>
.form-companies__logo-hint {
  margin: 0 0 10px;
  font-size: 0.8rem;
  color: var(--grey-color, #5f5f5f);
}
</style>
